import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { adminListWhatsAppPending, adminListWhatsAppHistory, adminMarkWhatsAppContacted, adminUnmarkWhatsAppContacted } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { MessageCircle, Phone, Check, RotateCcw, Search, Copy, ExternalLink, Loader2, Clock, History, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';

// Use unicode escape sequences to guarantee proper UTF-8 encoding of emojis
const EMOJI_HAND = '\uD83D\uDC4B';   // 👋
const EMOJI_TARGET = '\uD83C\uDFAF'; // 🎯
const EMOJI_CLOVER = '\uD83C\uDF40'; // 🍀

const DEFAULT_TEMPLATE_FR = `Bonjour {{firstName}} ${EMOJI_HAND}

Bienvenue chez WINLUXOR ! Votre compte vient d'être créé avec succès.

${EMOJI_TARGET} Si vous avez la moindre question sur nos concours, n'hésitez pas à m'écrire ici. Je suis disponible pour vous aider !

${EMOJI_CLOVER} Bonne chance et profitez bien de la plateforme !

— L'équipe WINLUXOR`;

const DEFAULT_TEMPLATE_EN = `Hi {{firstName}} ${EMOJI_HAND}

Welcome to WINLUXOR! Your account has just been created successfully.

${EMOJI_TARGET} If you have any questions about our competitions, feel free to message me here. I'm available to help!

${EMOJI_CLOVER} Good luck and enjoy the platform!

— The WINLUXOR team`;

// Plain version without emojis (for older devices that don't render them)
const PLAIN_TEMPLATE_FR = `Bonjour {{firstName}},

Bienvenue chez WINLUXOR ! Votre compte vient d'être créé avec succès.

Si vous avez la moindre question sur nos concours, n'hésitez pas à m'écrire ici. Je suis disponible pour vous aider !

Bonne chance et profitez bien de la plateforme.

— L'équipe WINLUXOR`;

const PLAIN_TEMPLATE_EN = `Hi {{firstName}},

Welcome to WINLUXOR! Your account has just been created successfully.

If you have any questions about our competitions, feel free to message me here. I'm available to help!

Good luck and enjoy the platform.

— The WINLUXOR team`;

export default function AdminWhatsAppContacts() {
  const { user, isAdmin, loading } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [marking, setMarking] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [template, setTemplate] = useState(() => {
    return localStorage.getItem('winluxor_wa_template_' + lang) || (lang === 'fr' ? DEFAULT_TEMPLATE_FR : DEFAULT_TEMPLATE_EN);
  });

  useEffect(() => {
    if (loading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, loading]);

  const loadAll = async () => {
    setRefreshing(true);
    try {
      const [p, h] = await Promise.all([adminListWhatsAppPending(), adminListWhatsAppHistory()]);
      setPending(p.data?.users || []);
      setHistory(h.data?.users || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setRefreshing(false);
    }
  };

  const cleanPhone = (phone) => {
    if (!phone) return '';
    // Strip everything except digits + optional leading +
    const cleaned = phone.replace(/[^\d+]/g, '');
    // wa.me requires no leading + and no spaces
    return cleaned.replace(/^\+/, '');
  };

  const buildMessage = (firstName, lastName) => {
    return template
      .replace(/\{\{firstName\}\}/g, firstName || '')
      .replace(/\{\{lastName\}\}/g, lastName || '')
      .replace(/\{\{fullName\}\}/g, `${firstName || ''} ${lastName || ''}`.trim());
  };

  const buildWaLink = (u) => {
    const phone = cleanPhone(u.phone);
    if (!phone) return null;
    const msg = buildMessage(u.first_name, u.last_name);
    // Use proper UTF-8 encoding (encodeURIComponent already handles 4-byte UTF-8 emojis natively).
    // We just need to make sure the message is treated as a JS string with proper unicode.
    const encoded = encodeURIComponent(msg);
    return `https://wa.me/${phone}?text=${encoded}`;
  };

  const handleClickWhatsApp = (u) => {
    const link = buildWaLink(u);
    if (!link) {
      toast.error('Numéro de téléphone manquant');
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const handleMarkContacted = async (userId) => {
    setMarking(userId);
    try {
      await adminMarkWhatsAppContacted(userId);
      toast.success('Marqué comme contacté ✓');
      await loadAll();
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setMarking(null);
    }
  };

  const handleRestore = async (userId) => {
    if (!window.confirm('Restaurer cet utilisateur dans la liste à contacter ?')) return;
    setMarking(userId);
    try {
      await adminUnmarkWhatsAppContacted(userId);
      toast.success('Restauré dans la liste');
      await loadAll();
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setMarking(null);
    }
  };

  const handleCopyMessage = (u) => {
    const msg = buildMessage(u.first_name, u.last_name);
    navigator.clipboard.writeText(msg);
    toast.success('Message copié dans le presse-papier');
  };

  const saveTemplate = () => {
    localStorage.setItem('winluxor_wa_template_' + lang, template);
    toast.success('Modèle sauvegardé');
  };

  const filteredPending = pending.filter((u) => {
    if (!searchQuery) return true;
    const s = searchQuery.toLowerCase();
    return (
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(s) ||
      (u.email || '').toLowerCase().includes(s) ||
      (u.phone || '').toLowerCase().includes(s)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex bg-cream">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-cream">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-3xl font-bold text-rolex-green mb-1 flex items-center gap-3">
                <MessageCircle className="w-8 h-8 text-emerald-600" />
                Suivi WhatsApp
              </h1>
              <p className="text-rolex-green/65 text-sm">
                Liste des nouveaux clients à contacter manuellement sur WhatsApp.
                Cliquez sur le numéro pour ouvrir WhatsApp Web avec le message pré-rempli.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-500 text-white text-base px-4 py-2 gap-2">
                <Clock className="w-4 h-4" />
                {pending.length} en attente
              </Badge>
              <Button variant="outline" onClick={loadAll} disabled={refreshing} className="gap-2">
                {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Rafraîchir
              </Button>
            </div>
          </div>

          <Tabs defaultValue="pending">
            <TabsList className="mb-5 bg-cream border border-gold/30">
              <TabsTrigger value="pending" className="gap-2">
                <Clock className="w-4 h-4" /> En attente ({pending.length})
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-2">
                <History className="w-4 h-4" /> Historique ({history.length})
              </TabsTrigger>
              <TabsTrigger value="template" className="gap-2">
                <SettingsIcon className="w-4 h-4" /> Modèle de message
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              <Card className="bg-white border-2 border-gold/20 shadow-lg">
                <CardHeader className="border-b border-gold/20">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <CardTitle className="text-rolex-green flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-600" />
                      Clients à contacter
                    </CardTitle>
                    <div className="relative w-full sm:w-72">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-rolex-green/45" />
                      <Input
                        placeholder="Rechercher par nom, email, téléphone…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 border-gold/30"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {filteredPending.length === 0 ? (
                    <div className="p-12 text-center">
                      <Check className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
                      <p className="font-display text-lg font-bold text-rolex-green mb-1">Aucun client en attente</p>
                      <p className="text-sm text-rolex-green/55">Tous les nouveaux clients ont été contactés. 🎉</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gold/10">
                      {filteredPending.map((u) => (
                        <div key={u._id} className="p-4 hover:bg-cream/40 transition-colors flex items-center gap-4 flex-wrap">
                          {/* Avatar */}
                          <div className="w-12 h-12 rounded-full bg-gold-gradient flex items-center justify-center shadow-md shrink-0">
                            <span className="font-display text-lg font-bold text-rolex-green">
                              {(u.first_name?.[0] || '?').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
                            </span>
                          </div>

                          {/* Infos */}
                          <div className="flex-1 min-w-[180px]">
                            <p className="font-display font-bold text-rolex-green">
                              {u.first_name} {u.last_name}
                            </p>
                            <p className="text-xs text-rolex-green/65">{u.email}</p>
                            <p className="text-[11px] text-rolex-green/45 mt-0.5">
                              📍 {u.country || '?'}{u.city ? ` · ${u.city}` : ''}
                              <span className="ml-2">🕐 {new Date(u.created_at).toLocaleString()}</span>
                            </p>
                          </div>

                          {/* Phone (clickable) */}
                          <a
                            href={buildWaLink(u) || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-mono font-bold text-sm shadow-md hover:shadow-lg transition-all"
                            data-testid={`wa-phone-${u._id}`}
                            title="Ouvrir WhatsApp Web"
                          >
                            <MessageCircle className="w-4 h-4" />
                            {u.phone || 'No phone'}
                            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                          </a>

                          {/* Action buttons */}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCopyMessage(u)}
                              className="border-gold/40 text-gold-dark hover:bg-gold/10 gap-1.5"
                              title="Copier le message"
                            >
                              <Copy className="w-3.5 h-3.5" />
                              Copier
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleMarkContacted(u._id)}
                              disabled={marking === u._id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                              data-testid={`mark-contacted-${u._id}`}
                            >
                              {marking === u._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              Marquer envoyé
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="mt-5 bg-blue-50 border-blue-200">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <MessageCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900 space-y-2">
                      <p className="font-bold">💡 Comment ça marche ?</p>
                      <ol className="text-xs space-y-1 list-decimal list-inside">
                        <li>Connectez votre WhatsApp à <a href="https://web.whatsapp.com" target="_blank" rel="noopener noreferrer" className="underline">web.whatsapp.com</a> sur votre ordinateur (1 fois)</li>
                        <li>Cliquez sur le bouton vert avec le numéro du client</li>
                        <li>WhatsApp Web s'ouvre directement avec le message pré-rempli</li>
                        <li>Vous appuyez sur "Envoyer" → message parti !</li>
                        <li>Cliquez sur "Marquer envoyé" pour le retirer de la liste</li>
                      </ol>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history">
              <Card className="bg-white border-2 border-gold/20 shadow-lg">
                <CardHeader className="border-b border-gold/20">
                  <CardTitle className="text-rolex-green flex items-center gap-2">
                    <History className="w-5 h-5 text-emerald-600" />
                    Clients déjà contactés
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {history.length === 0 ? (
                    <div className="p-10 text-center text-rolex-green/55">
                      <History className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p>Aucun historique pour l'instant</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gold/10">
                      {history.map((u) => (
                        <div key={u._id} className="p-4 hover:bg-cream/40 transition-colors flex items-center gap-4 flex-wrap">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                            <Check className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-[180px]">
                            <p className="font-bold text-rolex-green text-sm">{u.first_name} {u.last_name}</p>
                            <p className="text-xs text-rolex-green/65">{u.email} · {u.phone}</p>
                            <p className="text-[11px] text-rolex-green/45 mt-0.5">
                              ✓ Contacté le {u.whatsapp_contacted_at ? new Date(u.whatsapp_contacted_at).toLocaleString() : '?'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <a
                              href={buildWaLink(u) || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-mono"
                              title="Renvoyer un message"
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                              {u.phone}
                            </a>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRestore(u._id)}
                              disabled={marking === u._id}
                              className="text-amber-600 border-amber-200 hover:bg-amber-50 text-xs"
                            >
                              {marking === u._id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                              Restaurer
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="template">
              <Card className="bg-white border-2 border-gold/20 shadow-lg">
                <CardHeader className="border-b border-gold/20">
                  <CardTitle className="text-rolex-green flex items-center gap-2">
                    <SettingsIcon className="w-5 h-5 text-gold-dark" />
                    Modèle de message WhatsApp
                  </CardTitle>
                  <p className="text-xs text-rolex-green/65 mt-2">
                    Personnalisez le message envoyé. Variables disponibles : <code className="bg-cream px-1.5 rounded">{'{{firstName}}'}</code>, <code className="bg-cream px-1.5 rounded">{'{{lastName}}'}</code>, <code className="bg-cream px-1.5 rounded">{'{{fullName}}'}</code>
                  </p>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label>Message ({lang === 'fr' ? 'Français' : 'English'})</Label>
                    <Textarea
                      value={template}
                      onChange={(e) => setTemplate(e.target.value)}
                      rows={12}
                      className="font-mono text-sm border-gold/30"
                    />
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <p className="text-xs font-bold text-emerald-900 mb-2">📱 Aperçu (avec un client fictif "Marie Dupont") :</p>
                    <div className="bg-white rounded-lg p-3 border border-emerald-200 whitespace-pre-wrap text-sm text-rolex-green">
                      {buildMessage('Marie', 'Dupont')}
                    </div>
                  </div>
                  <Button onClick={saveTemplate} className="btn-gold gap-2">
                    <Check className="w-4 h-4" />
                    Sauvegarder le modèle
                  </Button>
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-gold/10">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setTemplate(lang === 'fr' ? DEFAULT_TEMPLATE_FR : DEFAULT_TEMPLATE_EN)}
                      className="text-xs border-gold/30 text-gold-dark hover:bg-gold/10"
                    >
                      🔄 Restaurer modèle (avec emojis)
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setTemplate(lang === 'fr' ? PLAIN_TEMPLATE_FR : PLAIN_TEMPLATE_EN)}
                      className="text-xs border-stone-300 text-stone-700 hover:bg-stone-50"
                    >
                      📝 Modèle simple (sans emojis)
                    </Button>
                  </div>
                  <p className="text-[11px] text-rolex-green/55 italic pt-2">
                    💡 Si les emojis apparaissent comme "?" sur certains téléphones, utilisez le modèle simple sans emojis. C'est plus compatible avec les anciens appareils.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
